import { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@inkblot-therapy/squid"
import { useFlags } from "launchdarkly-react-client-sdk"
import { FormQueryKeys } from "../../constants/FormQueryKeys"
import { DataBaseService } from "../../services/DatabaseService"
import { FeatureFlagConfig } from "../../types/FeatureFlags"

const onboardingInfo = [
  { province: "ontario", bulletPoints: 4 },
  { province: "novascotia", bulletPoints: 3 },
]

export function LandingPage() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const navigate = useNavigate()

  const { province } = useParams<{ province: string }>()
  const { useReferralVersion2 } = useFlags<FeatureFlagConfig>()
  const useMdsV2 = useReferralVersion2 && province === "ontario"
  const i18nKey = useMdsV2 ? "mainV2" : "main"

  const currentProvince = province ?? "ontario"
  const displayInfo = onboardingInfo.find(
    (oi) => oi.province === currentProvince.toLowerCase(),
  )

  const prefetchFormData = useCallback(async () => {
    await queryClient.prefetchQuery(
      FormQueryKeys.byProvince(currentProvince),
      () => DataBaseService.getFormByProvince(currentProvince),
    )
  }, [queryClient, currentProvince])

  useEffect(() => {
    prefetchFormData()
  }, [prefetchFormData])

  const onGetStarted = () => {
    navigate("../steps/1")
  }

  const onStartUpload = () => {
    navigate("../upload")
  }

  const renderRequirements = () => {
    const bulletPoints = []
    if (displayInfo !== undefined) {
      for (let i = 1; i <= displayInfo.bulletPoints; i++) {
        bulletPoints.push(
          <li key={t(`client.${currentProvince}.main.bullet${i}`)}>
            {t(`client.${currentProvince}.main.bullet${i}`)}
          </li>,
        )
      }
    }
    return bulletPoints
  }

  const renderActions = () => {
    if (!useMdsV2) {
      return (
        <div className="flex">
          {currentProvince === "ontario" && (
            <Button onClick={onStartUpload} variant="outlined" className="mr-5">
              {t("client.buttons.upload")}
            </Button>
          )}
          <Button
            onClick={onGetStarted}
            variant="primary"
            rightIcon="ArrowRight"
          >
            {t("client.buttons.getStarted")}
          </Button>
        </div>
      )
    }

    return (
      <div className="flex flex-row-reverse">
        <Button onClick={onStartUpload} variant="primary" className="w-40 p-2">
          {t("client.buttons.startReferral")}
        </Button>
      </div>
    )
  }

  return (
    <div
      className={
        useMdsV2
          ? `items-center max-w-2xl mx-auto mt-24`
          : "flex flex-col h-[100vh] justify-center items-center max-w-2xl mx-auto"
      }
    >
      <div className="flex flex-col">
        <div className="pb-[56px] flex flex-col">
          <div className="text-[color:var(--core-color-slate-90)] [font-family:var(--alias-font-display-xl-font-family)] [font-size:var(--alias-font-display-xl-font-size)]">
            <p>{t(`client.${currentProvince}.${i18nKey}.title`)}</p>
          </div>
          {t(`client.${currentProvince}.${i18nKey}.subtitle`) && (
            <p className="text-[color:var(--alias-color-text-weaker)] [font-family:var(--alias-font-display-md-font-family)] [font-size:var(--alias-font-display-md-font-size)]">
              {t(`client.${currentProvince}.${i18nKey}.subtitle`)}
            </p>
          )}
        </div>
        <div className="flex flex-col">
          <h2 className="text-[color:var(--core-color-slate-90)] [font-family:var(--alias-font-display-lg-font-family)] [font-size:var(--alias-font-display-lg-font-size)] pb-2">
            {t(`client.${currentProvince}.${i18nKey}.subtitle2`)}
          </h2>
          {useMdsV2 ? (
            <p>{t(`client.${currentProvince}.${i18nKey}.bullet1`)}</p>
          ) : (
            <ul className="list-disc list-inside">{renderRequirements()}</ul>
          )}
        </div>
        <div className="pb-[56px]" />
        {renderActions()}
      </div>
    </div>
  )
}
